import React, { useState, useEffect } from 'react';
import { 
  ChevronDown, ChevronRight, Check, X, Clock, 
  Settings, Zap, MessageSquare, Bell, Brain, 
  ArrowRight, BarChart3, Users, Palette,
  GitBranch, Play, Pause
} from 'lucide-react';
import getApiUrl from '../../utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


// ════════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL : VUE ARBORESCENCE
// ════════════════════════════════════════════════════════════════════════════════

const WorkflowTree = () => {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedStages, setExpandedStages] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/pipeline-workflow/stages`);
      const data = await res.json();
      setStages(data.stages || []);
      // Expand first stage by default
      if (data.stages?.length > 0) {
        setExpandedStages({ [data.stages[0].stage_id]: true });
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleStage = (stageId) => {
    setExpandedStages(prev => ({
      ...prev,
      [stageId]: !prev[stageId]
    }));
  };

  const expandAll = () => {
    const all = {};
    stages.forEach(s => all[s.stage_id] = true);
    setExpandedStages(all);
  };

  const collapseAll = () => {
    setExpandedStages({});
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin"></div>
          <span className="text-slate-400">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600/20 via-teal-600/20 to-cyan-600/20 border border-emerald-500/30 rounded-2xl p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/25">
              <GitBranch className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Arborescence Pipeline</h2>
              <p className="text-slate-400">Configuration détaillée par étape</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={expandAll}
              className="px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors"
            >
              Tout déplier
            </button>
            <button
              onClick={collapseAll}
              className="px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors"
            >
              Tout replier
            </button>
          </div>
        </div>

        {/* Légende */}
        <div className="flex items-center gap-6 mt-6 pt-4 border-t border-slate-700/50">
          <span className="text-xs text-slate-500 uppercase tracking-wider">Légende :</span>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded border-2 border-emerald-500 bg-emerald-500/20 flex items-center justify-center">
              <Check size={10} className="text-emerald-400" />
            </div>
            <span className="text-sm text-slate-400">Actif</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded border-2 border-amber-500 bg-amber-500/20 flex items-center justify-center">
              <Clock size={10} className="text-amber-400" />
            </div>
            <span className="text-sm text-slate-400">En attente</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded border-2 border-slate-600 bg-slate-800 flex items-center justify-center">
              <X size={10} className="text-slate-500" />
            </div>
            <span className="text-sm text-slate-400">Inactif</span>
          </div>
        </div>
      </div>

      {/* Liste des étapes en arborescence */}
      <div className="bg-slate-900/50 border border-slate-700 rounded-2xl overflow-hidden font-mono text-sm">
        {stages.filter(s => s.stage_actif).map((stage, index) => (
          <StageTreeNode
            key={stage.stage_id}
            stage={stage}
            stages={stages}
            isExpanded={expandedStages[stage.stage_id]}
            onToggle={() => toggleStage(stage.stage_id)}
            isLast={index === stages.filter(s => s.stage_actif).length - 1}
          />
        ))}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// COMPOSANT : NOEUD D'ÉTAPE DANS L'ARBRE
// ════════════════════════════════════════════════════════════════════════════════

const StageTreeNode = ({ stage, stages, isExpanded, onToggle, isLast }) => {
  const workflow = stage.workflow || {};
  const entryTriggers = workflow.entry_triggers || [];
  const entryActions = workflow.entry_actions || [];
  const exitTriggers = workflow.exit_triggers || [];

  return (
    <div className={`${!isLast ? 'border-b border-slate-800' : ''}`}>
      {/* Header de l'étape */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-4 hover:bg-slate-800/50 transition-colors text-left"
      >
        <span className="text-slate-600">{isExpanded ? '▼' : '▶'}</span>
        <div 
          className="w-3 h-3 rounded-full flex-shrink-0"
          style={{ backgroundColor: stage.stage_color }}
        />
        <span className="text-emerald-400 font-semibold">{stage.stage_name}</span>
        <span className="text-slate-600">({stage.stage_id})</span>
        
        {/* Mini stats */}
        <div className="flex items-center gap-2 ml-auto">
          {entryTriggers.filter(t => t.enabled).length > 0 && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400">
              {entryTriggers.filter(t => t.enabled).length} triggers
            </span>
          )}
          {entryActions.filter(a => a.enabled).length > 0 && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400">
              {entryActions.filter(a => a.enabled).length} actions
            </span>
          )}
          {exitTriggers.filter(t => t.enabled).length > 0 && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400">
              {exitTriggers.filter(t => t.enabled).length} sorties
            </span>
          )}
        </div>
      </button>

      {/* Contenu déplié */}
      {isExpanded && (
        <div className="pl-8 pb-4 space-y-1">
          {/* Infos de base */}
          <TreeLine prefix="├──" icon={<Settings size={14} />} label="Informations de base" color="text-slate-400">
            <TreeLeaf prefix="├──" label={`Nom: ${stage.stage_name}`} />
            <TreeLeaf prefix="├──" label={`Couleur: ${stage.stage_color}`} colorDot={stage.stage_color} />
            <TreeLeaf prefix="└──" label={`Dashboard: ${stage.stage_actif ? 'Actif' : 'Inactif'}`} />
          </TreeLine>

          {/* Automatisations */}
          <TreeLine prefix="├──" icon={<Zap size={14} />} label="AUTOMATISATIONS" color="text-amber-400">
            {/* Triggers d'entrée */}
            <TreeLine prefix="├──" icon={<Play size={12} />} label={`Triggers d'entrée (${entryTriggers.length})`} color="text-emerald-400" indent>
              {entryTriggers.length > 0 ? (
                entryTriggers.map((trigger, i) => (
                  <TreeLeaf 
                    key={trigger.id}
                    prefix={i === entryTriggers.length - 1 ? "└──" : "├──"}
                    label={trigger.description || `Trigger ${trigger.type}`}
                    status={trigger.enabled ? 'active' : 'inactive'}
                    type={trigger.type}
                  />
                ))
              ) : (
                <TreeLeaf prefix="└──" label="Aucun trigger configuré" muted />
              )}
            </TreeLine>

            {/* Actions automatiques */}
            <TreeLine prefix="├──" icon={<Bell size={12} />} label={`Actions automatiques (${entryActions.length})`} color="text-blue-400" indent>
              {entryActions.length > 0 ? (
                entryActions.map((action, i) => {
                  const isPending = !action.enabled && (action.type === 'sms' || action.type === 'email');
                  return (
                    <TreeLeaf 
                      key={action.id}
                      prefix={i === entryActions.length - 1 ? "└──" : "├──"}
                      label={`${action.type.toUpperCase()} → ${action.target}${action.delay_minutes ? ` (+${action.delay_minutes}min)` : ''}`}
                      status={action.enabled ? 'active' : isPending ? 'pending' : 'inactive'}
                      template={action.template_id}
                    />
                  );
                })
              ) : (
                <TreeLeaf prefix="└──" label="Aucune action configurée" muted />
              )}
            </TreeLine>

            {/* Triggers de sortie */}
            <TreeLine prefix="└──" icon={<ArrowRight size={12} />} label={`Triggers de sortie (${exitTriggers.length})`} color="text-purple-400" indent>
              {exitTriggers.length > 0 ? (
                exitTriggers.map((trigger, i) => {
                  const targetStage = stages.find(s => s.stage_id === trigger.target_stage);
                  return (
                    <TreeLeaf 
                      key={trigger.id}
                      prefix={i === exitTriggers.length - 1 ? "└──" : "├──"}
                      label={`${trigger.description || trigger.condition || 'Transition'}`}
                      status={trigger.enabled ? 'active' : 'inactive'}
                      target={targetStage?.stage_name}
                      targetColor={targetStage?.stage_color}
                    />
                  );
                })
              ) : (
                <TreeLeaf prefix="└──" label="Aucune sortie configurée" muted />
              )}
            </TreeLine>
          </TreeLine>

          {/* Métriques */}
          <TreeLine prefix="└──" icon={<BarChart3 size={14} />} label="Métriques" color="text-cyan-400">
            <TreeLeaf prefix="├──" label="Temps moyen: --" muted />
            <TreeLeaf prefix="└──" label="Taux conversion: --" muted />
          </TreeLine>
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// COMPOSANTS UTILITAIRES POUR L'ARBRE
// ════════════════════════════════════════════════════════════════════════════════

const TreeLine = ({ prefix, icon, label, color = "text-slate-300", children, indent = false }) => {
  const [isOpen, setIsOpen] = useState(true);
  
  return (
    <div className={indent ? "pl-4" : ""}>
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 py-1 hover:bg-slate-800/30 rounded px-1 w-full text-left"
      >
        <span className="text-slate-700 w-8">{prefix}</span>
        {icon && <span className={color}>{icon}</span>}
        <span className={`${color} font-medium`}>{label}</span>
        {children && (
          <span className="text-slate-600 text-xs ml-1">
            {isOpen ? '▾' : '▸'}
          </span>
        )}
      </button>
      {isOpen && children && (
        <div className="pl-8">
          {children}
        </div>
      )}
    </div>
  );
};

const TreeLeaf = ({ prefix, label, status, type, template, target, targetColor, colorDot, muted = false }) => {
  const getStatusIcon = () => {
    if (status === 'active') {
      return (
        <div className="w-4 h-4 rounded border-2 border-emerald-500 bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
          <Check size={10} className="text-emerald-400" />
        </div>
      );
    }
    if (status === 'pending') {
      return (
        <div className="w-4 h-4 rounded border-2 border-amber-500 bg-amber-500/20 flex items-center justify-center flex-shrink-0">
          <Clock size={10} className="text-amber-400" />
        </div>
      );
    }
    if (status === 'inactive') {
      return (
        <div className="w-4 h-4 rounded border-2 border-slate-600 bg-slate-800 flex items-center justify-center flex-shrink-0">
          <X size={10} className="text-slate-500" />
        </div>
      );
    }
    return null;
  };

  const getTypeLabel = () => {
    if (!type) return null;
    const types = {
      'auto': { label: 'Auto', color: 'text-amber-400 bg-amber-500/20' },
      'semi_auto': { label: 'Semi', color: 'text-blue-400 bg-blue-500/20' },
      'manual': { label: 'Manuel', color: 'text-slate-400 bg-slate-500/20' },
      'conversational': { label: 'Conv', color: 'text-purple-400 bg-purple-500/20' }
    };
    const t = types[type];
    return t ? (
      <span className={`text-xs px-1 rounded ${t.color}`}>{t.label}</span>
    ) : null;
  };

  return (
    <div className="flex items-center gap-2 py-0.5 pl-4">
      <span className="text-slate-700 w-8 flex-shrink-0">{prefix}</span>
      {getStatusIcon()}
      {colorDot && (
        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: colorDot }} />
      )}
      <span className={muted ? "text-slate-600 italic" : "text-slate-300"}>{label}</span>
      {getTypeLabel()}
      {template && (
        <span className="text-xs text-slate-500">(tpl: {template})</span>
      )}
      {target && (
        <span className="flex items-center gap-1 text-xs text-purple-400">
          <ArrowRight size={10} />
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: targetColor }} />
          {target}
        </span>
      )}
    </div>
  );
};

export default WorkflowTree;
